/**
 * OnboardingStep4 — documenti attivi dal registro, spunte granulari,
 * riepilogo e atto unico di presa visione.
 *
 * Il testo legale arriva a runtime dalla callable `getDocumentiLegaliAttivi`.
 * Qui non c'e' nessuna stringa dei documenti: titoli, etichette e caselle
 * sono quelli del registro. Le uniche copy sono di interfaccia
 * (obbligatorio/facoltativo, apri/chiudi, riepilogo, rifiuto di documento).
 *
 * Le caselle NON sono condizionate all'apertura del testo: leggere e'
 * un diritto, non un ostacolo. L'atto di conferma sta in fondo.
 *
 * Risposta: un documento entra nel payload solo se e' interamente accettato
 * o interamente rifiutato. Il non risposto e il parziale non arrivano
 * al riepilogo e bloccano l'invio (vedi risposta-documento.ts).
 */

import { useEffect, useId, useRef, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
} from 'react-hook-form';
import {
  etichettaConsenso,
  titoloCard,
  titoloDocumento,
  type DocumentoLegale,
  type SpecConsenso,
} from '@shared/legal/documento-legale.types';
import { DocumentoLegaleView } from '@shared/legal/DocumentoLegaleView';
import { CheckboxField } from '@shared/components/ui/FormField';
import type { OnboardingFormValues } from './onboarding-form-types';
import {
  MESSAGGIO_DOCUMENTO_SENZA_RISPOSTA,
  statoRispostaDocumento,
  usaRifiutoDiDocumento,
} from '@shared/legal/risposta-documento';

interface Step4Props {
  control: Control<OnboardingFormValues>;
  setValue: UseFormSetValue<OnboardingFormValues>;
  getValues: UseFormGetValues<OnboardingFormValues>;
  errors: FieldErrors<OnboardingFormValues>;
  documenti: DocumentoLegale[];
  voci: Record<string, boolean | undefined>;
  documentiSenzaRisposta: string[];
  segnalazioneSeq: number;
}

function scriviVoci(
  getValues: UseFormGetValues<OnboardingFormValues>,
  setValue: UseFormSetValue<OnboardingFormValues>,
  updates: Record<string, boolean | undefined>
): void {
  const next = { ...getValues('voci') };
  for (const [id, value] of Object.entries(updates)) {
    if (value === undefined) delete next[id];
    else next[id] = value;
  }
  setValue('voci', next, { shouldDirty: true, shouldTouch: true });
}

function sommarioDocumento(doc: DocumentoLegale): string {
  if (doc.sommario && doc.sommario.trim() !== '') return doc.sommario;
  return doc.bloccanteSeRifiutato
    ? 'Necessario per ricevere le prestazioni dello Studio.'
    : 'Facoltativo: puoi rifiutarlo senza conseguenze sull\u2019onboarding.';
}

function etichettaVoce(spec: SpecConsenso): string {
  return etichettaConsenso(spec) ?? spec.id;
}

function DocumentoCard({
  doc,
  control,
  setValue,
  getValues,
  voci,
  incompleto,
}: {
  doc: DocumentoLegale;
  control: Control<OnboardingFormValues>;
  setValue: UseFormSetValue<OnboardingFormValues>;
  getValues: UseFormGetValues<OnboardingFormValues>;
  voci: Record<string, boolean | undefined>;
  incompleto: boolean;
}) {
  const [aperto, setAperto] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titolo = titoloCard(doc);
  const titoloIntegrale = titoloDocumento(doc);
  const obbligatorio = doc.bloccanteSeRifiutato;
  const stato = statoRispostaDocumento(doc, voci);
  const rifiutato = stato === 'rifiutato';
  const erroreId = `errore-doc-${doc.documentoId}`;
  const conRifiutoDocumento = usaRifiutoDiDocumento(doc);

  useEffect(() => {
    if (aperto) {
      panelRef.current?.focus();
    }
  }, [aperto]);

  const toggle = () => {
    setAperto((prev) => {
      const next = !prev;
      if (!next) {
        queueMicrotask(() => buttonRef.current?.focus());
      }
      return next;
    });
  };

  const applicaRifiutoDocumento = (rifiuta: boolean) => {
    const updates: Record<string, boolean | undefined> = {};
    for (const spec of doc.consensi) {
      updates[spec.id] = rifiuta ? false : undefined;
    }
    scriviVoci(getValues, setValue, updates);
  };

  return (
    <article
      id={`documento-${doc.documentoId}`}
      tabIndex={-1}
      aria-invalid={incompleto || undefined}
      aria-describedby={incompleto ? erroreId : undefined}
      className={`rounded-xl border bg-surface-container-lowest p-4 flex flex-col gap-3 outline-none ${
        incompleto
          ? 'border-error ring-2 ring-error'
          : 'border-outline-variant'
      }`}
    >
      <header className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-text-main leading-snug">
            {titolo}
          </h2>
          <span
            className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
              obbligatorio
                ? 'bg-error-container text-on-error-container'
                : 'bg-tertiary-container text-on-tertiary-container'
            }`}
          >
            {obbligatorio ? 'Obbligatorio' : 'Facoltativo'}
          </span>
        </div>
        <p className="text-sm text-text-secondary">{sommarioDocumento(doc)}</p>
        {!obbligatorio && (
          <p className="text-sm text-text-secondary">
            Se rifiuti ora, potrai decidere diversamente in studio alla prima
            visita.
          </p>
        )}
        {incompleto && (
          <p id={erroreId} role="alert" className="text-sm text-error">
            {MESSAGGIO_DOCUMENTO_SENZA_RISPOSTA}
          </p>
        )}
      </header>

      <button
        ref={buttonRef}
        type="button"
        aria-expanded={aperto}
        aria-controls={panelId}
        onClick={toggle}
        className="self-start min-h-[44px] px-4 rounded-lg border border-outline text-text-main bg-surface-container-lowest font-medium hover:bg-surface-container"
      >
        {aperto ? 'Chiudi il documento' : 'Leggi il documento'}
      </button>

      {aperto && (
        <div
          id={panelId}
          ref={panelRef}
          tabIndex={-1}
          role="region"
          aria-label={titoloIntegrale}
          className="max-h-[min(24rem,60vh)] overflow-y-auto rounded-lg border border-outline-variant bg-surface-container-low p-3"
        >
          <DocumentoLegaleView
            blocchi={doc.blocchi}
            idPrefix={`${doc.documentoId}-testo`}
          />
        </div>
      )}

      <fieldset className="flex flex-col gap-2 pt-1">
        <legend className="text-sm font-medium text-text-main mb-1">
          Le tue scelte
        </legend>
        {doc.consensi.map((spec) => (
          <VoceConsensoField
            key={spec.id}
            spec={spec}
            control={control}
            setValue={setValue}
            getValues={getValues}
            accepted={voci[spec.id] === true}
            disabled={conRifiutoDocumento && rifiutato}
          />
        ))}
        {conRifiutoDocumento && (
          <div className="border-t border-outline-variant pt-2 mt-1">
            <CheckboxField
              id={`rifiuto-doc-${doc.documentoId}`}
              label="Rifiuto questo documento"
              checked={rifiutato}
              onChange={applicaRifiutoDocumento}
            />
          </div>
        )}
      </fieldset>
    </article>
  );
}

function VoceConsensoField({
  spec,
  control,
  setValue,
  getValues,
  accepted,
  disabled,
}: {
  spec: SpecConsenso;
  control: Control<OnboardingFormValues>;
  setValue: UseFormSetValue<OnboardingFormValues>;
  getValues: UseFormGetValues<OnboardingFormValues>;
  accepted: boolean;
  disabled: boolean;
}) {
  if (spec.tipo === 'binario') {
    const groupName = `consenso-${spec.id}`;
    const si = spec.etichettaSi ?? 'Sì';
    const no = spec.etichettaNo ?? 'No';
    return (
      <Controller
        name={`voci.${spec.id}`}
        control={control}
        render={({ field }) => {
          const value = field.value;
          return (
            <fieldset className="rounded-lg border border-outline-variant p-3">
              <legend className="text-sm text-text-main px-1">
                {etichettaVoce(spec)}
              </legend>
              <div className="flex flex-col gap-1 mt-1">
                <label
                  htmlFor={`${groupName}-si`}
                  className="flex items-start gap-3 cursor-pointer select-none min-h-[44px] py-2"
                >
                  <input
                    id={`${groupName}-si`}
                    type="radio"
                    name={groupName}
                    checked={value === true}
                    onChange={() => field.onChange(true)}
                    className="mt-1 h-5 w-5 border-outline text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-text-main leading-snug">
                    {si}
                  </span>
                </label>
                <label
                  htmlFor={`${groupName}-no`}
                  className="flex items-start gap-3 cursor-pointer select-none min-h-[44px] py-2"
                >
                  <input
                    id={`${groupName}-no`}
                    type="radio"
                    name={groupName}
                    checked={value === false}
                    onChange={() => field.onChange(false)}
                    className="mt-1 h-5 w-5 border-outline text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-text-main leading-snug">
                    {no}
                  </span>
                </label>
              </div>
            </fieldset>
          );
        }}
      />
    );
  }

  return (
    <CheckboxField
      id={`consenso-${spec.id}`}
      label={etichettaVoce(spec)}
      checked={accepted}
      disabled={disabled}
      onChange={(checked) => {
        scriviVoci(getValues, setValue, {
          [spec.id]: checked ? true : undefined,
        });
      }}
    />
  );
}

function Riepilogo({
  documenti,
  voci,
}: {
  documenti: DocumentoLegale[];
  voci: Record<string, boolean | undefined>;
}) {
  const accettate: string[] = [];
  const rifiutate: string[] = [];
  for (const doc of documenti) {
    const stato = statoRispostaDocumento(doc, voci);
    if (stato === 'non_risposto') continue;
    const titolo = titoloCard(doc);
    for (const spec of doc.consensi) {
      const riga = `${titolo}: ${etichettaVoce(spec)}`;
      if (voci[spec.id] === true) accettate.push(riga);
      else if (voci[spec.id] === false) rifiutate.push(riga);
    }
  }

  return (
    <section
      aria-labelledby="riepilogo-consensi-title"
      className="rounded-xl border border-outline bg-surface-container-low p-4 flex flex-col gap-3"
    >
      <h2
        id="riepilogo-consensi-title"
        className="text-base font-semibold text-text-main"
      >
        Riepilogo delle tue scelte
      </h2>
      <div>
        <h3 className="text-sm font-medium text-tertiary mb-1">Accetti</h3>
        {accettate.length === 0 ? (
          <p className="text-sm text-text-secondary">Nessuna dichiarazione.</p>
        ) : (
          <ul className="list-disc pl-5 space-y-1">
            {accettate.map((r) => (
              <li key={`ok-${r}`} className="text-sm text-text-main">
                {r}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-error mb-1">Rifiuti</h3>
        {rifiutate.length === 0 ? (
          <p className="text-sm text-text-secondary">Nessuna dichiarazione.</p>
        ) : (
          <ul className="list-disc pl-5 space-y-1">
            {rifiutate.map((r) => (
              <li key={`no-${r}`} className="text-sm text-text-main">
                {r}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function OnboardingStep4({
  control,
  setValue,
  getValues,
  errors,
  documenti,
  voci,
  documentiSenzaRisposta,
  segnalazioneSeq,
}: Step4Props) {
  const incompleti = new Set(documentiSenzaRisposta);

  useEffect(() => {
    if (segnalazioneSeq === 0) return;
    const primo = documentiSenzaRisposta[0];
    if (!primo) return;
    document.getElementById(`documento-${primo}`)?.focus();
  }, [segnalazioneSeq, documentiSenzaRisposta]);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-text-secondary">
        Leggi i documenti e indica, per ciascuno, cosa accetti e cosa rifiuti.
        InBody e Genius TSF sono facoltativi: rifiutarli non impedisce di
        completare l&apos;onboarding.
      </p>

      {documenti.map((doc) => (
        <DocumentoCard
          key={doc.documentoId}
          doc={doc}
          control={control}
          setValue={setValue}
          getValues={getValues}
          voci={voci}
          incompleto={incompleti.has(doc.documentoId)}
        />
      ))}

      <Riepilogo documenti={documenti} voci={voci} />

      <Controller
        name="confermaPresaVisione"
        control={control}
        render={({ field }) => (
          <CheckboxField
            id="conferma-presa-visione"
            required
            label="Dichiaro di aver preso visione dei documenti e di confermare le scelte sopra indicate."
            checked={field.value === true}
            onChange={field.onChange}
            error={errors.confermaPresaVisione?.message}
          />
        )}
      />
    </div>
  );
}
